<template>
  <WrapContainer
    v-model:active-key="state.activeKey"
    v-if="isWidget"
    :show-tab="useType === 'both'"
    @change="onActiveKeyChange"
    :style="{ zIndex: zIndex }"
  >
    <component :is="getComponentName" :key="getComponentName" v-bind="getBindArgs" />
  </WrapContainer>

  <template v-if="!isWidget">
    <div
      class="vc-color-wrap transparent"
      :class="{ round: shape === 'circle' }"
      ref="colorCubeRef"
      v-bind="$attrs"
    >
      <div class="current-color" :style="getBgColorStyle" @click="onShowPicker"></div>
    </div>

    <teleport to="body">
      <div ref="pickerRef" v-if="showPicker" :style="{ zIndex: zIndex }">
        <WrapContainer
          :show-tab="useType === 'both' && !state.isAdvanceMode"
          v-if="showPicker"
          v-model:active-key="state.activeKey"
          @change="onActiveKeyChange"
        >
          <component :is="getComponentName" :key="getComponentName" v-bind="getBindArgs" />
        </WrapContainer>
      </div>
    </teleport>
  </template>
</template>

<script lang="ts">
  import {
    computed,
    defineComponent,
    nextTick,
    onMounted,
    onUnmounted,
    onUpdated,
    provide,
    reactive,
    ref,
    watch,
    watchEffect,
  } from 'vue';
  import TkColorPicker from './tk/TkColorPicker.vue';
  import ChromeColorPicker from './chrome/ChromeColorPicker.vue';
  import GradientColorPicker from './gradient/GradientColorPicker.vue';
  import WrapContainer from './common/WrapContainer.vue';
  import tinycolor from 'tinycolor2';
  import { Color, HistoryColorKey, MAX_STORAGE_LENGTH } from './utils/color';
  import { onClickOutside, RemovableRef, useDebounceFn, useLocalStorage } from '@vueuse/core';
  import { createPopper } from '@popperjs/core';
  import { ColorPickerProviderKey } from './utils/type';
  import { parse, stringify } from 'gradient-parser';
  import type { GradientNode } from 'gradient-parser';
  import type { ColorPickerProvider, SupportLang } from './utils/type';
  import { colorPickerProps } from './props';
  import { useInitColorPicker } from '../../../hooks';

  export default defineComponent({
    name: 'ColorPicker',
    components: {
      TkColorPicker,
      ChromeColorPicker,
      GradientColorPicker,
      WrapContainer,
    },
    inheritAttrs: false,
    props: colorPickerProps,
    emits: [
      'update:modelValue',
      'on-undo',
      'colorChange',
      'afterColorChanged',
      'update:activeKey',
      'activeKeyChange',
    ],
    setup(props, { emit }) {
      const { pureColor, gradientColor, last } = useInitColorPicker(props);
      const lastColor = ref(last);

      const state = reactive({
        pureColor: pureColor,
        activeKey: props.useType === 'gradient' ? 'gradient' : props.activeKey, //  "pure" | "gradient"
        isAdvanceMode: false,
      });

      provide<ColorPickerProvider>(ColorPickerProviderKey, {
        lang: computed(() => props.lang || 'ZH-cn'),
      });

      const instance = new Color(state.pureColor);
      const colorInstance = ref(instance);

      const startColor = new Color('#fff');
      const endColor = new Color('#fff');
      const gradientState = reactive({
        startColor,
        endColor,
        startColorStop: 0,
        endColorStop: 100,
        angle: 0,
        gradientColor: gradientColor,
      });

      // Ref
      const showPicker = ref(false);
      const colorCubeRef = ref<any>(null);
      const pickerRef = ref<any>(null);

      watchEffect(() => {
        const { pureColor, gradientColor, last } = useInitColorPicker(props);
        state.pureColor = pureColor;
        gradientState.gradientColor = gradientColor;
        lastColor.value = last;
      });

      watch(
        () => showPicker.value,
        () => {
          console.log('showPicker.value ===> %o', showPicker.value);
        },
      );

      const getBgColorStyle = computed(() => {
        const bgColor =
          state.activeKey !== 'gradient'
            ? tinycolor(state.pureColor).toRgbString()
            : gradientState.gradientColor;
        return {
          background: bgColor,
        };
      });

      const getComponentName = computed(() => {
        if (state.activeKey === 'gradient') {
          return GradientColorPicker.name;
        }
        return props.pickerType === 'tk' ? TkColorPicker.name : ChromeColorPicker.name;
      });

      const getBindArgs = computed(() => {
        if (state.activeKey === 'gradient') {
          return {
            startColor: gradientState.startColor,
            endColor: gradientState.endColor,
            onStartColorChange: (v: Color) => {
              gradientState.startColor = v;
              onGradientChange();
            },
            onEndColorChange: (v: Color) => {
              gradientState.endColor = v;
              onGradientChange();
            },
            angle: gradientState.angle,
            startColorStop: gradientState.startColorStop,
            endColorStop: gradientState.endColorStop,
            onStartColorStopChange: (v: number) => {
              gradientState.startColorStop = v;
              onGradientChange();
            },
            onEndColorStopChange: (v: number) => {
              gradientState.endColorStop = v;
              onGradientChange();
            },
            onAngleChange: (v: number) => {
              gradientState.angle = v;
              onGradientChange();
            },
            onAdvanceChange: (v: boolean) => {
              state.isAdvanceMode = v;
            },
          };
        }
        return {
          disableAlpha: props.disableAlpha,
          disableHistory: props.disableHistory,
          roundHistory: props.roundHistory,
          color: colorInstance.value,
          onChange: onColorChange,
          onAdvanceChange,
        };
      });

      const onAdvanceChange = (isAdvance: boolean) => {
        state.isAdvanceMode = isAdvance;
      };

      const onShowPicker = () => {
        showPicker.value = true;
        nextTick(() => {
          createPopperInstance();
        });
      };

      const onHidePicker = () => {
        showPicker.value = false;
        if (lastColor.value !== state.pureColor) {
          const colorVal =
            state.activeKey === 'gradient' ? gradientState.gradientColor : state.pureColor;
          emit('on-undo', colorVal, lastColor.value);
          emit('update:modelValue', colorVal);
          emit('afterColorChanged', colorVal);
          lastColor.value = colorVal;
          updateColorHistoryFn();
        }

        // 销毁Popper
        destroyPopperInstance();
      };

      const historyColors: RemovableRef<string[]> = useLocalStorage<string[]>(
        HistoryColorKey,
        [],
        {},
      );

      const updateColorHistoryFn = useDebounceFn(() => {
        console.log('updateColorHistoryFn===>');
        debugger;
        if (props.disableHistory) {
          return;
        }

        const rgbString: any = state.pureColor;

        historyColors.value = historyColors.value.filter((value: any) => {
          return !tinycolor.equals(value, rgbString);
        });

        if (historyColors.value.includes(rgbString)) {
          return;
        }

        while (historyColors.value.length > MAX_STORAGE_LENGTH) {
          historyColors.value.pop();
        }

        historyColors.value.unshift(rgbString);
      }, 500);

      const parseGradientColor = () => {
        try {
          if (!gradientState.gradientColor) return;
          const [colorNode] = parse(gradientState.gradientColor);
          if (
            colorNode &&
            colorNode.type === 'linear-gradient' &&
            colorNode.orientation?.type === 'angular' &&
            colorNode.colorStops.length >= 2
          ) {
            const startColorVal = colorNode.colorStops[0];
            const endColorVal = colorNode.colorStops[1];

            gradientState.startColorStop = Number(startColorVal.length?.value) || 0;
            gradientState.endColorStop = Number(endColorVal.length?.value) || 0;
            gradientState.angle = Number(colorNode.orientation?.value) || 0;

            const [r, g, b, a] = startColorVal.value;
            const [r1, g1, b1, a1] = endColorVal.value;

            gradientState.startColor = new Color({
              r: Number(r),
              g: Number(g),
              b: Number(b),
              a: Number(a),
            });
            gradientState.endColor = new Color({
              r: Number(r1),
              g: Number(g1),
              b: Number(b1),
              a: Number(a1),
            });
          }
        } catch (e) {
          console.log(`[Parse Color]: ${e}`);
        }
      };

      const onGradientChange = useDebounceFn(() => {
        const nodes = color2GradientNode();
        try {
          gradientState.gradientColor = stringify(nodes);
          emit('update:modelValue', gradientState.gradientColor);
          emit('colorChange', gradientState.gradientColor);
        } catch (e) {
          console.log(e);
        }
      }, 100);

      const color2GradientNode = () => {
        const nodes: GradientNode[] = [];
        const startColorArr = gradientState.startColor.RGB.map((v) => v.toString());
        const endColorArr = gradientState.endColor.RGB.map((v) => v.toString());

        nodes.push({
          type: 'linear-gradient',
          orientation: { type: 'angular', value: gradientState.angle + '' },
          colorStops: [
            {
              type: 'rgba',
              value: [startColorArr[0], startColorArr[1], startColorArr[2], startColorArr[3]],
              length: { value: gradientState.startColorStop + '', type: '%' },
            },
            {
              type: 'rgba',
              value: [endColorArr[0], endColorArr[1], endColorArr[2], endColorArr[3]],
              length: { value: gradientState.endColorStop + '', type: '%' },
            },
          ],
        });

        return nodes;
      };

      let popperInstance: any = null;

      const createPopperInstance = () => {
        if (popperInstance) return;
        if (colorCubeRef.value && pickerRef.value) {
          const offsetParent = colorCubeRef.value.offsetParent;
          const offsetTop = colorCubeRef.value.offsetTop || offsetParent?.offsetTop;
          const offsetBottomHeight =
            window.innerHeight - (offsetTop - window.pageYOffset) - colorCubeRef.value.offsetHeight;
          createPopper(colorCubeRef.value, pickerRef.value, {
            placement: props.placement,
            modifiers: [
              {
                name: 'flip',
                options: {
                  enabled: true,
                  boundary: offsetBottomHeight > 500 ? 'clippingParents' : colorCubeRef.value,
                  fallbackPlacements: ['bottom', 'left'],
                },
              },
            ],
          });
        }
      };

      const destroyPopperInstance = () => {
        if (popperInstance) {
          popperInstance.destroy();
          popperInstance = null;
        }
      };

      onUnmounted(() => {
        destroyPopperInstance();
      });

      // const onInit = () => {
      //   if (colorCubeRef.value && pickerRef.value) {
      //     const offsetParent = colorCubeRef.value.offsetParent;
      //     const offsetTop = colorCubeRef.value.offsetTop || offsetParent?.offsetTop;
      //     const offsetBottomHeight =
      //       window.innerHeight - (offsetTop - window.pageYOffset) - colorCubeRef.value.offsetHeight;
      //     nextTick(() => {
      //       createPopper(colorCubeRef.value, pickerRef.value, {
      //         placement: props.placement,
      //         modifiers: [
      //           {
      //             name: 'flip',
      //             options: {
      //               boundary: offsetBottomHeight > 500 ? 'clippingParents' : colorCubeRef.value,
      //               fallbackPlacements: ['bottom', 'left'],
      //             },
      //           },
      //         ],
      //       });
      //     });
      //   }
      // };

      const onColorChange = (v: Color) => {
        colorInstance.value = v;
        state.pureColor = v.toString(props.format);
        emitColorChange();
      };

      const emitColorChange = useDebounceFn(() => {
        emit('update:modelValue', state.pureColor);
        emit('colorChange', state.pureColor);
      }, 100);

      onClickOutside(pickerRef, () => {
        console.log('onClickOutside ===>');
        onHidePicker();
      });

      const onActiveKeyChange = (key: string) => {
        state.activeKey = key;
        emit('update:activeKey', key);
        emit('activeKeyChange', key);
      };

      // tryOnMounted(() => {
      //
      // });

      // watch(
      //   () => pickerRef.value,
      //   (val) => {
      //     if (val) {
      //       onInit();
      //     }
      //   },
      // );

      onMounted(() => {
        parseGradientColor();
        // emitColorChange();
        // onGradientChange();
      });

      // onUpdated(() => {
      //   const { pureColor, gradientColor, last } = useInitColorPicker(props);
      //   state.pureColor = pureColor;
      //   gradientState.gradientColor = gradientColor;
      //   lastColor.value = last;
      // });

      watch(
        () => gradientColor,
        (value) => {
          if (value !== gradientState.gradientColor) {
            gradientState.gradientColor = value;
          }
        },
      );

      watch(
        () => gradientState.gradientColor,
        () => {
          parseGradientColor();
        },
      );

      watch(
        () => props.activeKey,
        (value) => {
          state.activeKey = value;
        },
      );

      watch(
        () => props.useType,
        (value) => {
          if (state.activeKey !== 'gradient' && value === 'gradient') {
            state.activeKey = 'gradient';
          } else {
            state.activeKey = 'pure';
          }
        },
      );

      watch(
        () => pureColor,
        (value) => {
          const equal = tinycolor.equals(value, state.pureColor);

          if (!equal) {
            state.pureColor = value;
            colorInstance.value = new Color(value);
            emitColorChange();
          }
        },
        { deep: true },
      );

      return {
        colorCubeRef,
        pickerRef,
        showPicker,
        colorInstance,
        getBgColorStyle,
        onColorChange,
        onShowPicker,
        onActiveKeyChange,
        getComponentName,
        getBindArgs,
        state,
      };
    },
  });
</script>

<style lang="scss">
  .vc-color-wrap {
    width: 50px;
    height: 24px;
    box-shadow: 3px 0 5px #00000014;
    position: relative;
    cursor: pointer;
    overflow: hidden;
    display: inline-block;
    vertical-align: middle;

    &.transparent {
      background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAwAAAAMCAIAAADZF8uwAAAAGUlEQVQYV2M4gwH+YwCGIasIUwhT25BVBADtzYNYrHvv4gAAAABJRU5ErkJggg==);
      background-repeat: repeat;
    }

    &.round {
      width: 22px;
      height: 22px;
      border-radius: 50%;
      border: 1px solid #d8d8d8;
    }

    .current-color {
      width: 100%;
      height: 100%;
    }
  }
</style>
